import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { useSettings } from '../hooks/useApi';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

function Hero() {
  const { t } = useLanguage();
  const { data: settingsData } = useSettings();
  const [statsRef, statsInView] = useInView({ threshold: 0.3, triggerOnce: true });

  const s = settingsData?.settings || settingsData || {};
  const heroText = s.hero_text || 'We Craft Interactive Digital Experiences';
  const heroSubtext = s.hero_subtext ||
    'A creative technology company specializing in web, virtual, on-ground interactive installations, and AI-powered solutions.';

  const stats = [
    { end: parseInt(s.years_experience, 10) || 16, suffix: '+', label: t('hero.yearsExp') },
    { end: parseInt(s.successful_projects, 10) || 250, suffix: '+', label: t('hero.projects') },
    { end: parseInt(s.total_clients, 10) || 90, suffix: '+', label: t('hero.clients') },
  ];

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      const offset = 72;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const scrollToPortfolio = () => {
    const el = document.getElementById('portfolio');
    if (el) {
      const offset = 72;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="hero">
      {/* Video Background */}
      <div className="hero__video-bg">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="hero__video"
        >
          <source src="/uploads/videos/hero/SHORT_immersive.mp4" type="video/mp4" />
        </video>
        <div className="hero__video-overlay" />
      </div>

      {/* Background effects */}
      <div className="hero__bg">
        <div className="hero__grid-overlay" />
      </div>

      <div className="hero__content">
        <div className="hero__text-wrapper">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="hero__tagline"
          >
            {t('hero.subtitle')}
          </motion.div>

          <motion.h1
            className="hero__title"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            {heroText.includes('Interactive') ? (
              <>
                {heroText.split('Interactive')[0]}
                <span>Interactive</span>
                {heroText.split('Interactive')[1]}
              </>
            ) : (
              heroText
            )}
          </motion.h1>

          <motion.p
            className="hero__description"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
          >
            {heroSubtext}
          </motion.p>

          <motion.div
            className="hero__actions"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
          >
            <button className="btn btn--primary btn--lg" onClick={scrollToContact}>
              {t('hero.cta')}
            </button>
            <button className="btn btn--outline btn--lg" onClick={scrollToPortfolio}>
              {t('hero.viewWork')}
            </button>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          className="hero__stats"
          ref={statsRef}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={4}
        >
          {stats.map((stat, idx) => (
            <div key={idx} className="hero__stat">
              <div className="hero__stat-number">
                {statsInView ? (
                  <CountUp
                    end={stat.end}
                    duration={2.5}
                    suffix={stat.suffix}
                    separator=","
                  />
                ) : (
                  `0${stat.suffix}`
                )}
              </div>
              <div className="hero__stat-label">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Hero;
